import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const TaxGuideDownload = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email capture to backend
      await axios.post(`${API}/guide-download`, { firstName, lastName, email });
      
      // Open the PDF in a new tab
      window.open('/reverse-mortgage-tax-guide.html', '_blank');
      
      setHasDownloaded(true);
    } catch (error) {
      // Error logged silently - user sees alert
      alert('Failed to process request. Please try again or call us at (720) 307-8711');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hasDownloaded) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Check Your Email!
            </h3>
            <p className="text-gray-600">
              Your Tax & Retirement Guide should have opened in a new tab. We've also emailed you a copy at <strong>{email}</strong>
            </p>
          </div>

          <Button 
            size="lg"
            variant="outline"
            className="w-full text-lg py-6"
            onClick={() => window.open('/reverse-mortgage-tax-guide.html', '_blank')}
          >
            <Download className="mr-2 w-5 h-5" />
            Open Guide Again
          </Button>

          <p className="text-sm text-gray-500">
            Didn't receive it? Check your spam folder or call us at (720) 307-8711
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
      <div className="text-center space-y-6">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
          <Download className="w-10 h-10 text-blue-600" />
        </div>
        
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Free Tax & Retirement Planning Guide
          </h3>
          <p className="text-gray-600">
            Download "Reverse Mortgage Tax & Retirement Planning Guide"
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 mb-3">What You'll Learn:</h4>
          <ul className="text-left space-y-2 text-gray-700">
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <span>Tax implications of reverse mortgage funds</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <span>Impact on Social Security and Medicare</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <span>Estate planning and wealth preservation</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <span>Strategic financial planning opportunities</span>
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-left">
            <Label htmlFor="download-firstName" className="text-base font-semibold">First Name</Label>
            <Input
              id="download-firstName"
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-2 text-base"
              placeholder="John"
            />
          </div>

          <div className="text-left">
            <Label htmlFor="download-lastName" className="text-base font-semibold">Last Name</Label>
            <Input
              id="download-lastName"
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="mt-2 text-base"
              placeholder="Smith"
            />
          </div>

          <div className="text-left">
            <Label htmlFor="download-email" className="text-base font-semibold">Email Address</Label>
            <Input
              id="download-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 text-base"
              placeholder="john@example.com"
            />
          </div>

          <Button 
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
          >
            <Download className="mr-2 w-5 h-5" />
            {isSubmitting ? 'Processing...' : 'Download Free Guide'}
          </Button>
        </form>

        <p className="text-sm text-gray-500">
          Learn how reverse mortgages can reduce tax burdens, preserve investment portfolios, and optimize retirement income—insights most loan officers can't provide.
        </p>
      </div>
    </div>
  );
};
